import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatDateTime } from '../ui/format';
import { Badge, ErrorState, Loading, PageHeader, Panel, type BadgeTone } from '../ui/Page';
import { ConfirmAction } from '../ui/ConfirmAction';
import { ConditionEditor } from './ConditionEditor';
import { StepList } from './StepEditor';
import {
  activateAutomation,
  deleteAutomation,
  getAutomation,
  listActions,
  listTriggers,
  pauseAutomation,
  saveAutomation,
  type AutomationDefinition,
  type AutomationDetail,
  type StepType,
} from './api';
import {
  addStep,
  countSteps,
  moveStep,
  problemsIn,
  removeStep,
  updateStep,
  type Path,
} from './steps';

/**
 * Building an automation.
 *
 * Three questions in order, because that is the order somebody thinks in: when does this run, what
 * does it do, and is it on. The trigger comes from the server's catalogue rather than a text box,
 * the steps are a list rather than a canvas, and nothing is saved until somebody presses save —
 * an automation that autosaved half a branch would be an automation acting on half a thought.
 *
 * Activating is deliberately separate from saving. A draft can be wrong and that is fine; a live
 * automation acts on real records without anybody watching, so turning it on is its own decision,
 * and the button refuses while the definition still has a hole in it.
 */

const TONE: Record<string, BadgeTone> = {
  ACTIVE: 'positive',
  PAUSED: 'warning',
  DRAFT: 'neutral',
  ARCHIVED: 'neutral',
};

export function AutomationBuilder(): React.JSX.Element {
  const { automationId = '' } = useParams();
  const client = useQueryClient();
  const navigate = useNavigate();

  const automation = useQuery({
    queryKey: ['automation', automationId],
    queryFn: () => getAutomation(automationId),
    enabled: automationId !== '',
  });

  const actions = useQuery({
    queryKey: ['automation-actions'],
    queryFn: listActions,
    staleTime: 60 * 60 * 1000,
  });

  const triggers = useQuery({
    queryKey: ['automation-triggers'],
    queryFn: listTriggers,
    staleTime: 60 * 60 * 1000,
  });

  const [draft, setDraft] = useState<AutomationDetail | null>(null);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  /*
   * The server's copy becomes the draft once, and again after a save.
   *
   * Keyed on the version rather than on the object, so a background refetch that changed nothing
   * does not throw away what somebody is in the middle of typing.
   */
  const version = automation.data?.version;
  useEffect(() => {
    if (automation.data) {
      setDraft(automation.data);
      setDirty(false);
    }
  }, [automation.data, version]);

  const requiredBy = useMemo(() => {
    const specs = new Map((actions.data?.actions ?? []).map((spec) => [spec.name, spec.requires]));

    return (action: string): readonly string[] => specs.get(action) ?? [];
  }, [actions.data]);

  const save = useMutation({
    mutationFn: (input: Parameters<typeof saveAutomation>[1]) =>
      saveAutomation(automationId, { ...input, expectedVersion: draft?.version }),
    onSuccess: (saved) => {
      setError(null);
      setDraft(saved);
      setDirty(false);
      void client.invalidateQueries({ queryKey: ['automations'] });
      void client.invalidateQueries({ queryKey: ['automation', automationId] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const toggle = useMutation({
    mutationFn: () =>
      draft?.status === 'ACTIVE' ? pauseAutomation(automationId) : activateAutomation(automationId),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['automation', automationId] });
      void client.invalidateQueries({ queryKey: ['automations'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const drop = useMutation({
    mutationFn: () => deleteAutomation(automationId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['automations'] });
      navigate('/automacoes');
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  if (automation.isLoading || actions.isLoading || triggers.isLoading) return <Loading />;
  if (automation.error) {
    return <ErrorState error={automation.error} onRetry={() => void automation.refetch()} />;
  }
  if (!draft) return <Loading />;

  const trigger = (triggers.data?.triggers ?? []).find((spec) => spec.type === draft.triggerType);
  const fields = trigger?.fields ?? ['event.type'];
  const problems = problemsIn(draft.definition.steps, requiredBy);
  const empty = draft.definition.steps.length === 0;

  const edit = (change: Partial<AutomationDetail>): void => {
    setDraft({ ...draft, ...change });
    setDirty(true);
  };

  const editDefinition = (next: AutomationDefinition): void => {
    setDraft({ ...draft, definition: next });
    setDirty(true);
  };

  return (
    <div className="view view--wide">
      <PageHeader
        title={draft.name}
        description={t('automations.builderHint')}
        actions={
          <div className="cell-inline">
            <Badge tone={TONE[draft.status] ?? 'neutral'}>
              {t(`automations.state.${draft.status}`)}
            </Badge>

            <Link className="button button--quiet button--compact" to="/automacoes">
              {t('record.backToList')}
            </Link>

            <button
              type="button"
              className="button button--compact"
              disabled={!dirty || save.isPending}
              onClick={() =>
                save.mutate({
                  name: draft.name,
                  description: draft.description,
                  triggerType: draft.triggerType,
                  ...(draft.triggerConditions
                    ? { triggerConditions: draft.triggerConditions }
                    : {}),
                  definition: draft.definition,
                })
              }
            >
              {save.isPending ? t('automations.saving') : t('automations.save')}
            </button>

            <button
              type="button"
              className="button button--primary button--compact"
              /*
               * Turning it on is refused while anything is unsaved or unfinished. Activating a
               * draft that does not match what is on screen would publish a version nobody read.
               */
              disabled={
                toggle.isPending ||
                (draft.status !== 'ACTIVE' && (dirty || empty || problems.length > 0))
              }
              title={
                draft.status !== 'ACTIVE' && (dirty || empty || problems.length > 0)
                  ? t('automations.fixFirst')
                  : undefined
              }
              onClick={() => toggle.mutate()}
            >
              {draft.status === 'ACTIVE' ? t('automations.pause') : t('automations.activate')}
            </button>
          </div>
        }
      />

      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      {/* `record` is narrow-then-wide, so the sidebar comes first in the markup and the builder
          gets the room. On a phone the grid collapses and the sidebar reads above the steps,
          which is the right order there too: the name before the machinery. */}
      <div className="record">
        <div className="stack">
          <Panel title={t('automations.details')}>
            <label className="stack__field">
              <span>{t('crm.field.name')}</span>
              <input
                value={draft.name}
                maxLength={200}
                onChange={(event) => edit({ name: event.target.value })}
              />
            </label>

            <label className="stack__field">
              <span>{t('crm.field.description')}</span>
              <textarea
                rows={3}
                value={draft.description ?? ''}
                maxLength={2000}
                onChange={(event) => edit({ description: event.target.value || null })}
              />
            </label>

            <dl className="facts">
              <dt>{t('automations.runs')}</dt>
              <dd>{draft.runCount}</dd>

              <dt>{t('automations.failuresLabel')}</dt>
              <dd>{draft.failureCount}</dd>

              <dt>{t('automations.lastRun')}</dt>
              <dd>{draft.lastRunAt ? formatDateTime(draft.lastRunAt) : '—'}</dd>
            </dl>

            <button
              type="button"
              className="button button--quiet button--danger button--compact"
              onClick={() => setRemoving(true)}
            >
              {t('social.delete')}
            </button>
          </Panel>

          <Panel title={t('automations.versions')}>
            {draft.versions.length === 0 ? (
              <p className="muted small">{t('automations.noVersions')}</p>
            ) : (
              <ul className="home-list">
                {draft.versions.map((entry) => (
                  <li key={entry.id}>
                    <span className="cell-stack">
                      <span className="small">
                        {t('automations.versionN').replace('{n}', String(entry.version))}
                        {entry.id === draft.publishedVersionId &&
                          ` · ${t('automations.published')}`}
                      </span>
                      {entry.notes && <span className="muted small">{entry.notes}</span>}
                    </span>
                    <span className="muted small">{formatDateTime(entry.createdAt)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
        <div className="stack">
          <Panel title={t('automations.when')}>
            <label className="stack__field">
              <span>{t('automations.trigger')}</span>
              <select
                className="select"
                value={draft.triggerType}
                onChange={(event) => edit({ triggerType: event.target.value })}
              >
                {!trigger && <option value={draft.triggerType}>{draft.triggerType}</option>}
                {(triggers.data?.triggers ?? []).map((spec) => (
                  <option key={spec.type} value={spec.type}>
                    {`${t(`automations.area.${spec.area}`)} · ${t(`automations.trigger.${spec.type}`)}`}
                  </option>
                ))}
              </select>
            </label>

            <div className="stack__field">
              <span>{t('automations.onlyWhen')}</span>
              <ConditionEditor
                value={draft.triggerConditions ?? { match: 'ALL', conditions: [] }}
                fields={fields}
                emptyHint={t('automations.noTriggerConditions')}
                onChange={(triggerConditions) => edit({ triggerConditions })}
              />
            </div>
          </Panel>

          <Panel
            title={`${t('automations.then')} · ${countSteps(draft.definition.steps)}`}
            actions={
              problems.length > 0 ? (
                <Badge tone="warning">
                  {t('automations.problems').replace('{n}', String(problems.length))}
                </Badge>
              ) : undefined
            }
          >
            {empty && <p className="muted">{t('automations.noSteps')}</p>}

            <StepList
              steps={draft.definition.steps}
              path={[]}
              actions={actions.data?.actions ?? []}
              fields={fields}
              problems={problems}
              onAdd={(into: Path, type: StepType) =>
                editDefinition(addStep(draft.definition, into, type))
              }
              onChange={(at, change) => editDefinition(updateStep(draft.definition, at, change))}
              onRemove={(at) => editDefinition(removeStep(draft.definition, at))}
              onMove={(at, direction) => editDefinition(moveStep(draft.definition, at, direction))}
            />
          </Panel>
        </div>
      </div>

      <ConfirmAction
        open={removing}
        title={t('automations.deleteTitle')}
        description={t('automations.deleteHint')}
        confirmLabel={t('social.delete')}
        onCancel={() => setRemoving(false)}
        onConfirm={() => {
          setRemoving(false);
          drop.mutate();
        }}
      />
    </div>
  );
}
